import { useState, type FormEvent } from "react";
import { useAction, useMutation, usePaginatedQuery } from "convex/react";
import { Pencil, Plus, Trash2, UserX } from "lucide-react";
import { api } from "../../convex/_generated/api";
import type { Id } from "../../convex/_generated/dataModel";
import {
  Badge,
  Button,
  Card,
  ConfirmDialog,
  EmptyState,
  Field,
  Input,
  Modal,
  Notice,
  PageHeader,
  TableSkeleton,
} from "../components/ui";
import { errorMessage, formatDate, ROLE_LABELS } from "../lib/format";
import { WlsLogo } from "../components/WlsLogo";

type UserRow = {
  _id: Id<"users">;
  name?: string;
  discordId?: string;
  isOwner: boolean;
  role: "admin" | "viewer" | "visitor";
  disabled: boolean;
  createdAt: number;
  avatarUrl?: string | null;
};

function CreateUserModal({ onClose }: { onClose: () => void }) {
  const create = useMutation(api.usersAdmin.create);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [created, setCreated] = useState<{ name: string; discordId: string } | null>(null);

  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    const name = String(form.get("name") ?? "");
    const discordId = String(form.get("discordId") ?? "");
    setLoading(true);
    setError("");
    try {
      await create({
        name,
        discordId,
        role: String(form.get("role")) as "admin" | "viewer" | "visitor",
      });
      setCreated({ name, discordId: discordId.trim() });
    } catch (reason) {
      setError(errorMessage(reason));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      title={created ? "Accès Discord ajouté" : "Ajouter un utilisateur"}
      description={created ? undefined : "Seul cet ID Discord pourra ouvrir le compte avec le rôle choisi."}
      onClose={onClose}
    >
      {created ? (
        <>
          <Notice tone="success">
            {created.name} peut maintenant se connecter avec l’ID Discord {created.discordId}.
          </Notice>
          <div className="modal-actions"><Button onClick={onClose}>Fermer</Button></div>
        </>
      ) : (
        <form onSubmit={submit} className="form-stack">
          <div className="form-grid">
            <Field label="Nom affiché"><Input name="name" required minLength={2} autoFocus /></Field>
            <Field label="Rôle">
              <select name="role" className="input" defaultValue="viewer">
                <option value="visitor">Visiteur</option>
                <option value="viewer">Lecture seule</option>
                <option value="admin">Administrateur</option>
              </select>
            </Field>
          </div>
          <Field label="ID Discord" hint="Activez le mode développeur Discord, puis utilisez « Copier l’identifiant utilisateur ». ">
            <Input name="discordId" inputMode="numeric" pattern="[0-9]{17,20}" required spellCheck={false} />
          </Field>
          {error && <Notice tone="error">{error}</Notice>}
          <div className="modal-actions">
            <Button variant="secondary" onClick={onClose}>Annuler</Button>
            <Button type="submit" loading={loading}>Autoriser cet ID</Button>
          </div>
        </form>
      )}
    </Modal>
  );
}

function DiscordIdModal({ user, onClose }: { user: UserRow; onClose: () => void }) {
  const setDiscordId = useAction(api.usersAdmin.setDiscordId);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const discordId = String(new FormData(event.currentTarget).get("discordId") ?? "");
    setLoading(true);
    setError("");
    try {
      await setDiscordId({ userId: user._id, discordId });
      onClose();
    } catch (reason) {
      setError(errorMessage(reason));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal title="Modifier l’ID Discord" description={`Compte : ${user.name || "Sans nom"}`} onClose={onClose}>
      <form onSubmit={submit} className="form-stack">
        <Field label="ID Discord">
          <Input
            name="discordId"
            defaultValue={user.discordId ?? ""}
            inputMode="numeric"
            pattern="[0-9]{17,20}"
            required
            autoFocus
            spellCheck={false}
          />
        </Field>
        <Notice tone="warning">Changer cet ID ferme les sessions du compte et retire son ancien rattachement Discord.</Notice>
        {error && <Notice tone="error">{error}</Notice>}
        <div className="modal-actions">
          <Button variant="secondary" onClick={onClose}>Annuler</Button>
          <Button type="submit" loading={loading}>Enregistrer</Button>
        </div>
      </form>
    </Modal>
  );
}

export function UsersPage() {
  const { results, status, loadMore } = usePaginatedQuery(api.usersAdmin.list, {}, { initialNumItems: 30 });
  const setRole = useMutation(api.usersAdmin.setRole);
  const disable = useAction(api.usersAdmin.disable);
  const removeUser = useAction(api.usersAdmin.remove);
  const [createOpen, setCreateOpen] = useState(false);
  const [editUser, setEditUser] = useState<UserRow | null>(null);
  const [confirmDisable, setConfirmDisable] = useState<UserRow | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<UserRow | null>(null);
  const [rowError, setRowError] = useState("");
  const [pendingRow, setPendingRow] = useState<Id<"users"> | null>(null);

  const changeRole = async (user: UserRow, role: "admin" | "viewer" | "visitor") => {
    setPendingRow(user._id);
    setRowError("");
    try {
      await setRole({ userId: user._id, role });
    } catch (reason) {
      setRowError(errorMessage(reason));
    } finally {
      setPendingRow(null);
    }
  };

  return (
    <>
      <PageHeader
        title="Utilisateurs"
        description="Autorisez les IDs Discord et attribuez leurs permissions. Aucun mot de passe local n’est accepté."
        actions={<Button onClick={() => setCreateOpen(true)}><Plus size={16} /> Ajouter un ID Discord</Button>}
      />
      {rowError && <Notice tone="error">{rowError}</Notice>}
      {status === "LoadingFirstPage" ? <TableSkeleton /> : results.length === 0 ? (
        <EmptyState title="Aucun utilisateur" description="Ajoutez un ID Discord pour autoriser un membre du staff." />
      ) : (
        <Card className="table-card">
          <div className="table-scroll">
            <table>
              <thead>
                <tr>
                  <th scope="col">Utilisateur</th>
                  <th scope="col">ID Discord</th>
                  <th scope="col">Rôle</th>
                  <th scope="col">État</th>
                  <th scope="col">Créé le</th>
                  <th scope="col" className="align-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {(results as UserRow[]).map((user) => (
                  <tr key={user._id}>
                    <td>
                      <div className="user-cell">
                        <span className="user-cell__avatar">
                          {user.avatarUrl ? <img src={user.avatarUrl} alt="" /> : <WlsLogo size={20} />}
                        </span>
                        <span className="identity-cell">
                          <strong>{user.name || "Sans nom"}</strong>
                        </span>
                      </div>
                    </td>
                    <td><code className="mono-wrap">{user.discordId ?? "Non attribué"}</code></td>
                    <td>
                      <select
                        className="input input--compact"
                        value={user.role}
                        disabled={pendingRow === user._id}
                        aria-label={`Rôle de ${user.name || "cet utilisateur"}`}
                        title={ROLE_LABELS[user.role]}
                        onChange={(event) => void changeRole(user, event.target.value as "admin" | "viewer" | "visitor")}
                      >
                        <option value="visitor">Visiteur</option>
                        <option value="viewer">Lecture seule</option>
                        <option value="admin">Administrateur</option>
                      </select>
                    </td>
                    <td><Badge tone={user.disabled ? "danger" : "success"}>{user.disabled ? "Désactivé" : "Actif"}</Badge></td>
                    <td><span className="nowrap">{formatDate(user.createdAt)}</span></td>
                    <td>
                      <div className="row-actions">
                        <button type="button" className="icon-button" title="Modifier l’ID Discord" aria-label="Modifier l’ID Discord" onClick={() => setEditUser(user)}><Pencil size={16} /></button>
                        {!user.disabled && user.role !== "admin" && (
                          <button type="button" className="icon-button icon-button--danger" title="Désactiver le compte" aria-label="Désactiver le compte" onClick={() => setConfirmDisable(user)}><UserX size={16} /></button>
                        )}
                        {!user.isOwner && (
                          <button type="button" className="icon-button icon-button--danger" title="Supprimer définitivement" aria-label="Supprimer définitivement" onClick={() => setConfirmDelete(user)}><Trash2 size={16} /></button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {status === "CanLoadMore" && <div className="load-more"><Button variant="secondary" onClick={() => loadMore(30)}>Afficher plus</Button></div>}
        </Card>
      )}
      {createOpen && <CreateUserModal onClose={() => setCreateOpen(false)} />}
      {editUser && <DiscordIdModal user={editUser} onClose={() => setEditUser(null)} />}
      {confirmDelete && (
        <ConfirmDialog
          title="Supprimer le compte"
          description="Le compte et son accès Discord disparaissent définitivement. Ses actions passées restent dans les logs."
          target={`${confirmDelete.name || "Sans nom"}${confirmDelete.discordId ? ` · ${confirmDelete.discordId}` : ""}`}
          confirmLabel="Supprimer"
          onConfirm={() => removeUser({ userId: confirmDelete._id })}
          onClose={() => setConfirmDelete(null)}
        />
      )}
      {confirmDisable && (
        <ConfirmDialog
          title="Désactiver le compte"
          description="Toutes les sessions seront fermées et cet ID Discord ne pourra plus se connecter."
          target={confirmDisable.name || "Compte sans nom"}
          confirmLabel="Désactiver"
          onConfirm={() => disable({ userId: confirmDisable._id })}
          onClose={() => setConfirmDisable(null)}
        />
      )}
    </>
  );
}
